// packages/web/src/components/learner/LearnerInterviewView.tsx
// Vista del alumno para InterviewPanel. NO contiene lógica de negocio:
// InterviewPanel sigue dueño del cronómetro, MediaRecorder, anti-pegado,
// metadata y ayudas, y le pasa estado + handlers (ver INTEGRACION.md).
import { useEffect, useRef, useState } from 'react';
import { Clock, HelpCircle, Loader2, Mic, Pause, PenLine, Send, Square, X } from 'lucide-react';
import { ALL_DIMS, type ChatTurn, type Dim, type HelpType } from '../../types';
import { DIM_KID, GuideAvatar } from './ui';

type TextGuards = Pick<React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onFocus' | 'onPaste' | 'onCopy' | 'onCut' | 'onContextMenu' | 'onDrop'>;

export interface LearnerInterviewViewProps {
  topicLabel: string;
  chatHistory: ChatTurn[];
  isAwaitingFirst: boolean;
  hasQuestion: boolean;            // !!lastCoach
  currentDim: Dim | null;
  remainingSec: number;
  paused: boolean;                 // isProcessing
  timeExpired: boolean;
  closing: boolean;
  allowText: boolean;
  allowAudio: boolean;
  answerText: string;
  onAnswerChange: (v: string) => void;
  textGuards: TextGuards;
  pasteBlockedFlash: boolean;
  onSubmitText: () => void;
  recording: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  recordingError: string | null;
  error: string | null;
  grantingType: HelpType | null;
  helpJustGranted: boolean;        // !!recentGrant
  onGrantHelp: (h: HelpType) => void;
  onCloseEarly: () => void;
}

// Mismos HelpType que HELP_LABEL; las penalizaciones no se muestran al alumno.
const HELPS: { k: HelpType; icon: string; t: string; s: string }[] = [
  { k: 'aclaracion', icon: 'Aa', t: '¿Qué significa una palabra?', s: 'Te explico la palabra difícil' },
  { k: 'reformulacion', icon: '↻', t: 'Pregúntame de otra forma', s: 'La misma pregunta, más fácil' },
  { k: 'pista_parcial', icon: '✦', t: 'Dame una pista', s: 'Una ayudita para empezar' },
  { k: 'pista_significativa', icon: '◎', t: 'Explícame la idea', s: 'Repasamos la idea juntos' },
];
const mmss = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export function LearnerInterviewView(p: LearnerInterviewViewProps) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [voiceMode, setVoiceMode] = useState(!p.allowText && p.allowAudio);
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [p.chatHistory.length, p.paused]);
  useEffect(() => { if (p.helpJustGranted) setHelpOpen(false); }, [p.helpJustGranted]);

  const idx = p.currentDim ? ALL_DIMS.indexOf(p.currentDim) : 0;
  const K = DIM_KID[p.currentDim ?? 'recuperacion'];
  const low = p.remainingSec <= 120;
  const locked = p.paused || p.timeExpired;
  const showVoice = p.allowAudio && (voiceMode || !p.allowText);

  return (
    <div className="flex h-[100dvh] flex-col bg-crema font-kid text-slate-900">
      <header className="border-b border-crema-300 bg-white px-4 pb-3 pt-4">
        <div className="mx-auto flex max-w-[640px] flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <div role="timer" aria-label={`Quedan ${mmss(p.remainingSec)}${p.paused ? ', pausado' : ''}`}
              className={`flex items-center gap-2 rounded-full px-3 py-2 text-lg font-bold tabular-nums ${p.paused ? 'bg-slate-100 text-slate-700' : low ? 'bg-amber-100 text-amber-800' : 'bg-marzano-50 text-marzano-600'}`}>
              {p.paused ? <Pause aria-hidden className="h-4 w-4" /> : <Clock aria-hidden className="h-4 w-4" />}
              {mmss(p.remainingSec)}
              {(p.paused || low) && <span className="text-[13px]">{p.paused ? 'pausado' : 'quedan'}</span>}
            </div>
            <span className="truncate text-[15px] text-slate-700">{p.topicLabel}</span>
            <button type="button" onClick={() => setConfirmClose(true)} disabled={locked || p.closing}
              className="btn-danger min-h-[44px] rounded-xl px-3.5 text-[15px]">
              {p.closing ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}Terminar
            </button>
          </div>
          <div className="grid grid-cols-4 gap-1" aria-hidden>
            {ALL_DIMS.map((d, i) => <span key={d} className={`h-1.5 rounded-full ${i <= idx ? DIM_KID[d].bar : 'bg-crema-300'}`} />)}
          </div>
          <p className="text-[15px] text-slate-700"><strong className={K.text}>Paso {idx + 1} de 4</strong> · {K.kid}</p>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        <div className="mx-auto flex max-w-[640px] flex-col gap-3.5" aria-live="polite">
          {p.isAwaitingFirst && <p className="flex items-center justify-center gap-2 py-8 text-slate-600"><Loader2 aria-hidden className="h-5 w-5 animate-spin" />Pío está preparando la primera pregunta…</p>}
          {p.chatHistory.map((t, i) => <Bubble key={i} turn={t} />)}
          {p.paused && !p.isAwaitingFirst && <ThinkingBubble />}
        </div>
      </div>

      {p.hasQuestion && (
        <footer className="border-t border-crema-300 bg-white px-4 pb-[max(24px,env(safe-area-inset-bottom))] pt-3">
          <div className={`mx-auto flex max-w-[640px] flex-col gap-2.5 ${p.paused ? 'opacity-55' : ''}`}>
            {p.timeExpired && <p role="alert" className="rounded-2xl bg-marzano-50 px-4 py-3 text-[17px] font-bold text-marzano-600">Se acabó el tiempo. Pío está revisando tus respuestas…</p>}
            {showVoice ? (
              <VoicePanel {...p} canWrite={p.allowText} onWrite={() => setVoiceMode(false)} onHelp={() => setHelpOpen(true)} locked={locked} />
            ) : (
              <>
                <button type="button" onClick={() => setHelpOpen(true)} disabled={locked}
                  className="inline-flex min-h-[44px] items-center gap-2 self-start rounded-full bg-amber-100 px-3.5 font-bold text-amber-800 disabled:opacity-50">
                  <HelpCircle aria-hidden className="h-5 w-5" />No entendí la pregunta
                </button>
                <label htmlFor="learner-answer" className="sr-only">Tu respuesta</label>
                <textarea id="learner-answer" value={p.answerText} onChange={(e) => p.onAnswerChange(e.target.value)} {...p.textGuards}
                  autoComplete="off" autoCorrect="off" spellCheck={false} rows={3} disabled={locked}
                  placeholder={p.paused ? 'Espera a Pío…' : 'Escribe tu respuesta aquí'}
                  className={`min-h-24 w-full resize-none rounded-[18px] border-2 px-3.5 py-3 text-lg leading-snug placeholder:text-slate-500 disabled:border-slate-300 ${p.pasteBlockedFlash ? 'border-amber-500' : 'border-marzano-600'}`} />
                {p.pasteBlockedFlash && <p role="alert" className="text-[15px] font-bold text-amber-800">Aquí no se puede copiar ni pegar. Escribe con tus palabras.</p>}
                <div className="flex gap-2.5">
                  {p.allowAudio && (
                    <button type="button" onClick={() => setVoiceMode(true)} disabled={locked} aria-label="Responder hablando"
                      className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-[18px] border-2 border-slate-300 text-marzano-600"><Mic aria-hidden className="h-6 w-6" /></button>
                  )}
                  <button type="button" onClick={p.onSubmitText} disabled={locked || !p.answerText.trim()}
                    className="btn-kid h-[60px] flex-1 bg-marzano-600 text-[19px] text-white disabled:bg-slate-300 disabled:text-slate-700">
                    {p.paused ? <><Loader2 aria-hidden className="h-5 w-5 animate-spin" />Enviando…</> : <>Enviar <Send aria-hidden className="h-5 w-5" /></>}
                  </button>
                </div>
              </>
            )}
            {(p.error || p.recordingError) && <p role="alert" className="rounded-2xl bg-red-50 px-4 py-3 text-[16px] text-red-800">{p.error ?? p.recordingError}</p>}
          </div>
        </footer>
      )}

      {helpOpen && (
        <Sheet title="¿Qué te ayudaría?" onClose={() => setHelpOpen(false)}>
          <p className="text-base text-slate-600">Elige una. Pío te ayuda y siguen conversando.</p>
          {HELPS.map((h) => (
            <button key={h.k} type="button" onClick={() => p.onGrantHelp(h.k)} disabled={p.grantingType !== null}
              className="flex min-h-16 items-center gap-3.5 rounded-[18px] border-2 border-crema-300 px-4 py-3 text-left hover:border-marzano-600 disabled:opacity-60">
              <span aria-hidden className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-marzano-50 text-xl text-marzano-600">
                {p.grantingType === h.k ? <Loader2 className="h-5 w-5 animate-spin" /> : h.icon}
              </span>
              <span className="flex flex-col"><strong className="text-lg">{h.t}</strong><span className="text-sm text-slate-600">{h.s}</span></span>
            </button>
          ))}
        </Sheet>
      )}
      {confirmClose && (
        <Sheet title="¿Quieres terminar ahora?" onClose={() => setConfirmClose(false)}>
          <p className="text-[17px] text-slate-700">Pío revisará lo que respondiste hasta aquí. Después no podrás seguir conversando.</p>
          <button type="button" onClick={() => { setConfirmClose(false); p.onCloseEarly(); }} className="btn-kid bg-red-700 text-white">Sí, terminar</button>
          <button type="button" onClick={() => setConfirmClose(false)} className="btn-kid border-2 border-slate-300 text-marzano-600">Seguir respondiendo</button>
        </Sheet>
      )}
    </div>
  );
}

function VoicePanel(p: LearnerInterviewViewProps & { canWrite: boolean; onWrite: () => void; onHelp: () => void; locked: boolean }) {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    if (!p.recording) { setSec(0); return; }
    const t0 = Date.now();
    const id = window.setInterval(() => setSec(Math.floor((Date.now() - t0) / 1000)), 250);
    return () => window.clearInterval(id);
  }, [p.recording]);
  const label = p.recording ? 'Toca para parar' : 'Toca para hablar';
  return (
    <div className="flex flex-col items-center gap-3.5 pt-2">
      <p role="status" className={`text-lg font-bold ${p.recording ? 'text-red-700' : 'text-slate-700'}`}>
        {p.recording ? `● Grabando ${mmss(sec).replace(/^0/, '')}` : p.paused ? 'Pío está escuchando tu respuesta…' : 'Toca el botón y habla'}
      </p>
      <button type="button" onClick={p.recording ? p.onStopRecording : p.onStartRecording} disabled={!p.recording && p.locked} aria-pressed={p.recording}
        className={`flex h-24 w-24 items-center justify-center rounded-full text-white ring-[10px] disabled:opacity-50 ${p.recording ? 'bg-red-700 ring-red-100' : 'bg-marzano-600 ring-marzano-50'}`}>
        {p.recording ? <Square aria-hidden className="h-9 w-9" /> : <Mic aria-hidden className="h-10 w-10" />}
        <span className="sr-only">{label}</span>
      </button>
      <span aria-hidden className="text-[17px] font-bold text-marzano-600">{label}</span>
      <div className={`grid w-full gap-2.5 ${p.canWrite ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <button type="button" onClick={p.onHelp} disabled={p.locked || p.recording} className="btn-kid min-h-[52px] bg-amber-100 text-base text-amber-800 disabled:opacity-50">
          <HelpCircle aria-hidden className="h-5 w-5" />No entendí
        </button>
        {p.canWrite && (
          <button type="button" onClick={p.onWrite} disabled={p.recording} className="btn-kid min-h-[52px] border-2 border-slate-300 text-base text-marzano-600">
            <PenLine aria-hidden className="h-5 w-5" />Escribir
          </button>
        )}
      </div>
    </div>
  );
}

function Bubble({ turn }: { turn: ChatTurn }) {
  if (turn.role === 'coach') {
    return (
      <div className="flex items-end gap-2">
        <GuideAvatar size={36} />
        <div className={`max-w-[85%] rounded-[20px] rounded-bl-md px-4 py-3.5 text-lg leading-snug ${turn.kind === 'help' ? 'bg-amber-50' : 'bg-white'}`}>
          {turn.kind === 'help' && <span className="mb-1 block text-sm font-bold text-amber-800">Ayuda de Pío</span>}
          <p className="whitespace-pre-wrap">{turn.text}</p>
        </div>
      </div>
    );
  }
  // La señal aiSuspicion es solo para el docente: no se muestra aquí.
  return <p className="max-w-[85%] self-end whitespace-pre-wrap rounded-[20px] rounded-br-md bg-marzano-600 px-4 py-3.5 text-lg leading-snug text-white">{turn.text}</p>;
}

/** Reemplaza TurnProcessingProgress para el alumno. Mismos cortes (0–4 s, 4–9 s, 9 s+). */
function ThinkingBubble() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t0 = Date.now();
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 250);
    return () => window.clearInterval(id);
  }, []);
  const i = elapsed < 4 ? 0 : elapsed < 9 ? 1 : 2;
  const titles = ['Pío está leyendo tu respuesta…', 'Pío está pensando…', 'Pío está preparando la próxima pregunta…'];
  return (
    <div className="flex items-end gap-2">
      <GuideAvatar size={36} />
      <div role="status" className="flex min-w-[240px] flex-col gap-2.5 rounded-[20px] rounded-bl-md bg-white px-4 py-3.5">
        <span className="text-[17px] font-bold">{titles[i]}</span>
        <div className="flex gap-3.5 text-sm font-bold" aria-hidden>
          {['Lee', 'Piensa', 'Prepara'].map((s, k) => <span key={s} className={k <= i ? 'text-marzano-600' : 'text-slate-400'}>{k <= i ? '●' : '○'} {s}</span>)}
        </div>
        <span className="text-sm text-slate-600">El reloj se detiene mientras espero.</span>
      </div>
    </div>
  );
}

function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const f = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-marzano-600/45" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}
        className="flex w-full max-w-[640px] flex-col gap-3 rounded-t-[28px] bg-white px-5 pb-[max(28px,env(safe-area-inset-bottom))] pt-3 shadow-sheet">
        <span aria-hidden className="h-[5px] w-11 self-center rounded-full bg-slate-300" />
        <div className="flex items-center justify-between">
          <h2 className="text-[22px] font-bold text-marzano-600">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="flex h-11 w-11 items-center justify-center rounded-full"><X aria-hidden className="h-6 w-6" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
